import { useCallback } from "react";

import { langCodeToJapanese } from "@/lib/chillnn/lang-code";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { viewModelToPlanCustomMailSettingInputConverter } from "./composition/view-model-to-plan-custom-mail-setting-input";
import { viewModelToPlanInputConverter } from "./composition/view-model-to-plan-input";
import { PlanI18nUpdateMutation } from "./mutation";

import type { ProductPlanI18nFormModel } from "../../../type";

type Props = {
	onResetViewModel: () => void;
};

export const usePlanEditMutation = ({ onResetViewModel }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { mutationFunc } = useCustomMutation(PlanI18nUpdateMutation);
	const onUpdatePlan = useCallback(
		async (
			planI18nViewModel: ProductPlanI18nFormModel["ViewModel"], //
		) => {
			showLoadingScreenCallback();

			const { lang } = planI18nViewModel;
			const langCodeText = langCodeToJapanese(lang);

			const input = viewModelToPlanInputConverter({ viewModel: planI18nViewModel });
			const planCustomMailInput = viewModelToPlanCustomMailSettingInputConverter({
				viewModel: planI18nViewModel,
			});

			try {
				// プランI18n更新
				const { errors: planErrors } = await mutationFunc({
					variables: {
						input,
						planCustomMailSettingI18nInput: planCustomMailInput,
					},
				});

				if (planErrors) {
					return;
				}

				notifyUserEventInfo({
					variant: "default",
					title: `プラン（${langCodeText}）を更新しました`,
				});

				onResetViewModel();
			} finally {
				hideLoadingScreenCallback();
			}
		},
		[hideLoadingScreenCallback, mutationFunc, onResetViewModel, showLoadingScreenCallback],
	);

	return { onUpdatePlan };
};
